/**
 * Helpers cliente para la API de Structura Market.
 * Sesión HttpOnly (cookie) + CSRF double-submit; tenant en localStorage.
 */
import { createClientRequestHeaders } from "@structura/core/client-headers";
import { parsePlanApiError, type PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { parsePermissionDeniedError, type PermissionDeniedPayload } from "@structura/core/permission-api-errors";
import { resolveWorkspaceTenantId as resolveWorkspaceTenantIdCore } from "@structura/core/workspace-tenant";
import {
  isSmCookiePrimaryTransport,
  shouldSmPersistBearerInClient,
  SM_CSRF_COOKIE_NAME,
} from "@/lib/sm-session-bridge";

const AUTH_KEY = "sm.authToken";
const TENANT_KEY = "sm.tenantId";

function isE2EBrowser(): boolean {
  return typeof window !== "undefined" && (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost");
}

// Visual/E2E runs use a freshly generated Bearer token in localStorage. Keep it
// available even if the server-side transport env resolves to cookie-primary.
if (typeof window !== "undefined" && isSmCookiePrimaryTransport() && !isE2EBrowser()) {
  localStorage.removeItem(AUTH_KEY);
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  if (isSmCookiePrimaryTransport() && !isE2EBrowser()) return null;
  return localStorage.getItem(AUTH_KEY);
}

export function getCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${SM_CSRF_COOKIE_NAME}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

let csrfEnsureInflight: Promise<string | null> | null = null;

export async function ensureCsrfToken(): Promise<string | null> {
  const existing = getCsrfToken();
  if (existing) return existing;
  if (typeof window === "undefined") return null;
  if (!csrfEnsureInflight) {
    csrfEnsureInflight = (async () => {
      try {
        const res = await fetch("/api/auth/csrf", { credentials: "include" });
        if (!res.ok) return null;
        const data = (await res.json()) as { csrf?: string };
        return data.csrf ?? getCsrfToken();
      } catch {
        return null;
      } finally {
        csrfEnsureInflight = null;
      }
    })();
  }
  return csrfEnsureInflight;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (shouldSmPersistBearerInClient() || isE2EBrowser()) {
    const t = getAuthToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const csrf = getCsrfToken();
  if (csrf) headers["X-CSRF-Token"] = csrf;
  return headers;
}

export function setAuthToken(token: string): void {
  if (isSmCookiePrimaryTransport() && !isE2EBrowser()) {
    localStorage.removeItem(AUTH_KEY);
    return;
  }
  localStorage.setItem(AUTH_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_KEY);
}

export function purgeLegacyAuthStorage(): void {
  clearAuthToken();
}

export function getStoredTenantId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TENANT_KEY);
}

export function setStoredTenantId(id: string): void {
  localStorage.setItem(TENANT_KEY, id);
}

export function clearStoredTenantId(): void {
  localStorage.removeItem(TENANT_KEY);
}

export const clientRequestHeaders = createClientRequestHeaders({
  getStoredTenantId,
  authHeaders,
  alwaysIncludeTenantHeader: false,
});

function tenantHeaders(tenantId?: string | null): Record<string, string> {
  return clientRequestHeaders(tenantId);
}

export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  tenantId?: string | null,
): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    await ensureCsrfToken();
  }
  return fetch(input, {
    ...init,
    credentials: "include",
    headers: {
      ...clientRequestHeaders(tenantId),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
}

function throwApiError(r: Response, data: unknown): never {
  const d = data as { error?: string; code?: string; limit?: number; current?: number; feature?: string };
  const planErr = parsePlanApiError(data);
  const permissionErr = parsePermissionDeniedError(data);
  const err = new Error(d.error ?? r.statusText) as Error & {
    quota?: import("@/components/QuotaLimitModal").QuotaLimitPayload;
    planLock?: PlanApiErrorPayload;
    permissionDenied?: PermissionDeniedPayload;
    status?: number;
    code?: string;
    body?: unknown;
  };
  err.status = r.status;
  err.code = d.code;
  err.body = data;
  if (planErr) {
    if (planErr.code === "plan_limit_reached" || planErr.code === "quota_exceeded") {
      err.quota = {
        message: planErr.message,
        code: planErr.code,
        limit: planErr.limit,
        current: planErr.current,
      };
    } else {
      err.planLock = planErr;
    }
  } else if (permissionErr) {
    err.permissionDenied = permissionErr;
  }
  throw err;
}

export async function apiGetJson<T>(path: string, tenantId?: string | null): Promise<T> {
  const r = await fetch(path, { credentials: "include", headers: tenantHeaders(tenantId) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throwApiError(r, data);
  return data as T;
}

// POS tax quotes are debounced at the caller, but cart mutations can still
// leave an older quote in flight while a newer quote is requested. Keep only
// the latest quote request alive so a stale tax snapshot cannot win a race.
let posTaxQuoteAbortController: AbortController | null = null;

function isPosTaxQuoteRequest(path: string): boolean {
  return path === "/api/tax/quote";
}

export async function apiSendJson<T>(
  path: string,
  method: string,
  body: unknown,
  tenantId?: string | null,
): Promise<T> {
  const isPosTaxQuote = isPosTaxQuoteRequest(path);
  const controller = isPosTaxQuote && typeof AbortController !== "undefined" ? new AbortController() : null;

  if (controller) {
    posTaxQuoteAbortController?.abort();
    posTaxQuoteAbortController = controller;
  }

  try {
    const r = await fetch(path, {
      method,
      headers: { ...tenantHeaders(tenantId), "Content-Type": "application/json" } as HeadersInit,
      body: JSON.stringify(body),
      credentials: "include",
      ...(controller ? { signal: controller.signal } : {}),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throwApiError(r, data);
    return data as T;
  } finally {
    if (controller && posTaxQuoteAbortController === controller) {
      posTaxQuoteAbortController = null;
    }
  }
}

export async function apiDelete(path: string, tenantId?: string | null): Promise<void> {
  const r = await fetch(path, { method: "DELETE", credentials: "include", headers: tenantHeaders(tenantId) });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throwApiError(r, data);
  }
}

export function setSuperAdminInApp(enabled: boolean): void {
  if (typeof window === "undefined") return;
  if (enabled) sessionStorage.setItem("sm.superAdminInApp", "1");
  else sessionStorage.removeItem("sm.superAdminInApp");
}

export function isSuperAdminInApp(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem("sm.superAdminInApp") === "1";
}

export function resolveWorkspaceTenantId(
  user: { role?: string | null; tenantId?: string | null },
  opts: { superAdminInApp: boolean; storedTenantId: string | null },
): string | null {
  return resolveWorkspaceTenantIdCore(user, opts, "fm");
}
